import type { AxiosInstance } from "axios";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  NotificationDelivery,
  NotificationDeliveryFilters,
  NotificationsPaginatedResponse,
  NotificationSummary,
  NotificationTemplate,
} from "~/types/admin/notifications";
import type {
  WhatsAppAction,
  WhatsAppActionConfigPayload,
  WhatsAppActionOverview,
  WhatsAppActionTestPayload,
  WhatsAppActionTestResult,
  WhatsAppAutoReplyConfig,
  WhatsAppMarketingSuppressionFilters,
  WhatsAppMarketingSuppressionRemovalPayload,
  WhatsAppMarketingSuppressionRemovalResult,
  WhatsAppMarketingSuppressionsPaginatedResponse,
  WhatsAppTemplate,
} from "~/types/admin/whatsappNotifications";

class AdminWhatsAppNotificationsModule extends HttpFactory {
  async getTemplates(): Promise<WhatsAppTemplate[]> {
    return await this.get(ENDPOINTS.ADMIN.NOTIFICATIONS.WHATSAPP.TEMPLATES);
  }

  async getActions(): Promise<WhatsAppAction[]> {
    return await this.get(ENDPOINTS.ADMIN.NOTIFICATIONS.WHATSAPP.ACTIONS);
  }

  async getOverview(): Promise<WhatsAppActionOverview> {
    return await this.get(ENDPOINTS.ADMIN.NOTIFICATIONS.WHATSAPP.OVERVIEW);
  }

  async getAutoReply(): Promise<WhatsAppAutoReplyConfig> {
    return await this.get(ENDPOINTS.ADMIN.NOTIFICATIONS.WHATSAPP.AUTO_REPLY);
  }

  async getMarketingSuppressions(
    filters: WhatsAppMarketingSuppressionFilters = {},
  ): Promise<WhatsAppMarketingSuppressionsPaginatedResponse> {
    return await this.get(
      ENDPOINTS.ADMIN.NOTIFICATIONS.WHATSAPP.MARKETING_SUPPRESSIONS,
      filters,
    );
  }

  async removeMarketingSuppression(
    suppressionId: number,
    payload: WhatsAppMarketingSuppressionRemovalPayload,
  ): Promise<WhatsAppMarketingSuppressionRemovalResult> {
    return await this.delete(
      `${ENDPOINTS.ADMIN.NOTIFICATIONS.WHATSAPP.MARKETING_SUPPRESSIONS}${suppressionId}/`,
      { data: payload },
    );
  }

  async updateActionConfig(
    actionKey: string,
    payload: WhatsAppActionConfigPayload,
  ): Promise<WhatsAppAction> {
    const encodedActionKey = encodeURIComponent(actionKey);
    return await this.patch(
      `${ENDPOINTS.ADMIN.NOTIFICATIONS.WHATSAPP.ACTIONS}${encodedActionKey}/`,
      payload,
    );
  }

  async testAction(
    actionKey: string,
    payload: WhatsAppActionTestPayload,
  ): Promise<WhatsAppActionTestResult> {
    const encodedActionKey = encodeURIComponent(actionKey);
    return await this.post(
      `${ENDPOINTS.ADMIN.NOTIFICATIONS.WHATSAPP.ACTIONS}${encodedActionKey}/test/`,
      payload,
    );
  }
}

class AdminNotificationsModule extends HttpFactory {
  readonly whatsapp: AdminWhatsAppNotificationsModule;

  constructor(axiosInstance: AxiosInstance) {
    super(axiosInstance);
    this.whatsapp = new AdminWhatsAppNotificationsModule(axiosInstance);
  }

  async getDeliveries(
    filters: NotificationDeliveryFilters = {},
  ): Promise<NotificationsPaginatedResponse<NotificationDelivery>> {
    return await this.get(ENDPOINTS.ADMIN.NOTIFICATIONS.DELIVERIES, filters);
  }

  async getDelivery(deliveryId: number): Promise<NotificationDelivery> {
    return await this.get(
      `${ENDPOINTS.ADMIN.NOTIFICATIONS.DELIVERIES}${deliveryId}/`,
    );
  }

  async retryDelivery(deliveryId: number): Promise<NotificationDelivery> {
    return await this.post(
      `${ENDPOINTS.ADMIN.NOTIFICATIONS.DELIVERIES}${deliveryId}/retry/`,
      {},
    );
  }

  async getSummary(
    filters: NotificationDeliveryFilters = {},
  ): Promise<NotificationSummary> {
    return await this.get(
      `${ENDPOINTS.ADMIN.NOTIFICATIONS.DELIVERIES}summary/`,
      filters,
    );
  }

  async getTemplates(): Promise<NotificationTemplate[]> {
    return await this.get(ENDPOINTS.ADMIN.NOTIFICATIONS.TEMPLATES);
  }
}

export default AdminNotificationsModule;
export { AdminWhatsAppNotificationsModule };
